import { FC, useMemo, useState, useCallback, useEffect } from 'react';
import {
    Table,
    TableColumnConfig,
    TextInput,
    Select,
    Switch,
    Button,
    Progress,
    Icon,
    Pagination,
    Flex,
    Text,
    Label,
    Tooltip,
    Checkbox,
} from '@gravity-ui/uikit';
import { TrashBin, CirclePlay } from '@gravity-ui/icons';
import { UploadFileItem } from '../../../hooks/useMultipleVideoUpload';
import { ProfilesRecord } from '../../Page/types/ProfilesRecord';
import { formatFileSize } from '../../../libs/formaters/formatFileSize';
import { MAX_VIDEO_TITLE_LENGTH } from '../../../constants/MAX_VIDEO_TITLE_LENGTH';
import { getProfileOptions, getDefaultProfile } from '../../../libs/utils/getProfileOptions';
import { commonT, errorsT, formsT, statusesT, videoListT, videoUploadT } from '../../../i18n';
import '../styles/MultipleUploadTable.css';

type MultipleUploadTableProps = {
    files: UploadFileItem[];
    profiles: ProfilesRecord;
    isUploading: boolean;
    onUpdateTitle: (id: string, title: string) => void;
    onUpdateProfile: (id: string, profile: string) => void;
    onToggleSummarization: (id: string) => void;
    onRemove: (id: string) => void;
    onStartSingle: (id: string) => void;
    onStartAll: () => void;
    onStopUpload: () => void;
    onCancel: () => void;
    onBulkUpdateProfile?: (ids: string[], profile: string) => void;
    onBulkToggleSummarization?: (ids: string[], enabled: boolean) => void;
};

const PAGE_SIZE_OPTIONS = [5, 10, 20];
const DEFAULT_PAGE_SIZE = 10;

export const MultipleUploadTable: FC<MultipleUploadTableProps> = ({
    files,
    profiles,
    isUploading,
    onUpdateTitle,
    onUpdateProfile,
    onToggleSummarization,
    onRemove,
    onStartSingle,
    onStartAll,
    onStopUpload,
    onCancel,
    onBulkUpdateProfile,
    onBulkToggleSummarization,
}) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());

    const profileOptions = useMemo(() => getProfileOptions(profiles), [profiles]);

    const paginatedFiles = useMemo(() => {
        const startIndex = (page - 1) * pageSize;
        return files.slice(startIndex, startIndex + pageSize);
    }, [files, page, pageSize]);

    const handlePageUpdate = (newPage: number, newPageSize: number) => {
        setPage(newPage);
        setPageSize(newPageSize);
    };

    useEffect(() => {
        const successFiles = files.filter((f) => f.status === 'success');

        successFiles.forEach((file) => {
            if (!removingIds.has(file.id)) {
                setTimeout(() => {
                    setRemovingIds((prev) => new Set(prev).add(file.id));
                }, 1000);
            }
        });
    }, [files, removingIds]);

    useEffect(() => {
        if (page > 1 && paginatedFiles.length === 0) {
            setPage(Math.max(1, Math.ceil(files.length / pageSize)));
        }
    }, [files.length, page, pageSize, paginatedFiles.length]);

    useEffect(() => {
        setSelectedIds((prev) => {
            const fileIds = new Set(files.map((f) => f.id));
            const newSelection = new Set<string>();
            prev.forEach((id) => {
                if (fileIds.has(id)) {
                    newSelection.add(id);
                }
            });
            return newSelection;
        });
    }, [files]);

    const handleSelectAll = useCallback(() => {
        const editableFiles = files.filter(
            (f) => f.status !== 'uploading' && f.status !== 'success',
        );
        if (selectedIds.size === editableFiles.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(editableFiles.map((f) => f.id)));
        }
    }, [files, selectedIds.size]);

    const getRowClassNames = useCallback(
        (item: UploadFileItem) => {
            const classes = [];
            if (removingIds.has(item.id)) {
                classes.push('upload-row-removing');
            }
            if (item.status === 'success' && !removingIds.has(item.id)) {
                classes.push('upload-row-success');
            }
            return classes;
        },
        [removingIds],
    );

    const handleSelectOne = useCallback((id: string, checked: boolean) => {
        setSelectedIds((prev) => {
            const newSet = new Set(prev);
            if (checked) {
                newSet.add(id);
            } else {
                newSet.delete(id);
            }
            return newSet;
        });
    }, []);

    const handleBulkProfileChange = useCallback(
        (profile: string) => {
            if (onBulkUpdateProfile && selectedIds.size > 0) {
                onBulkUpdateProfile(Array.from(selectedIds), profile);
            }
        },
        [onBulkUpdateProfile, selectedIds],
    );

    const handleBulkSummarizationChange = useCallback(
        (enabled: boolean) => {
            if (onBulkToggleSummarization && selectedIds.size > 0) {
                onBulkToggleSummarization(Array.from(selectedIds), enabled);
            }
        },
        [onBulkToggleSummarization, selectedIds],
    );

    const getStatusLabel = (item: UploadFileItem) => {
        switch (item.status) {
            case 'uploading':
                return <Label theme="info">{statusesT('uploading')}</Label>;
            case 'success':
                return <Label theme="success">{statusesT('success')}</Label>;
            case 'error':
                return (
                    <Tooltip content={item.error || errorsT('unknownError')}>
                        <span style={{ cursor: 'help' }}>
                            <Label theme="danger">{statusesT('error')}</Label>
                        </span>
                    </Tooltip>
                );
            default:
                return <Label theme="unknown">{statusesT('pending')}</Label>;
        }
    };

    const editableFiles = files.filter((f) => f.status !== 'uploading' && f.status !== 'success');
    const allEditableSelected =
        editableFiles.length > 0 && selectedIds.size === editableFiles.length;
    const someSelected = selectedIds.size > 0;

    const columns: TableColumnConfig<UploadFileItem>[] = useMemo(() => {
        const baseColumns: TableColumnConfig<UploadFileItem>[] = [
            {
                id: 'select',
                name: () => (
                    <Checkbox
                        checked={allEditableSelected}
                        indeterminate={someSelected && !allEditableSelected}
                        onUpdate={handleSelectAll}
                        disabled={isUploading || editableFiles.length === 0}
                    />
                ),
                width: 40,
                template: (item) => {
                    const isEditable = item.status !== 'uploading' && item.status !== 'success';
                    return (
                        <Checkbox
                            checked={selectedIds.has(item.id)}
                            onUpdate={(checked) => handleSelectOne(item.id, checked)}
                            disabled={!isEditable || isUploading}
                        />
                    );
                },
            },
            {
                id: 'title',
                name: videoListT('columnTitle'),
                width: 200,
                template: (item) => (
                    <TextInput
                        value={item.title}
                        placeholder={item.file.name}
                        onUpdate={(value) => onUpdateTitle(item.id, value)}
                        disabled={isUploading || item.status === 'success'}
                        validationState={
                            item.title.length > MAX_VIDEO_TITLE_LENGTH ? 'invalid' : undefined
                        }
                        size="m"
                    />
                ),
            },
            {
                id: 'profile',
                name: videoListT('columnProfile'),
                width: 150,
                template: (item) => (
                    <Select
                        value={item.profile ? [item.profile] : [getDefaultProfile(profiles)]}
                        options={profileOptions}
                        onUpdate={([value]) => {
                            if (selectedIds.has(item.id) && selectedIds.size > 1) {
                                handleBulkProfileChange(value);
                            } else {
                                onUpdateProfile(item.id, value);
                            }
                        }}
                        disabled={isUploading || item.status === 'success'}
                        width="max"
                        size="m"
                        placeholder={formsT('selectPlaceholder')}
                    />
                ),
            },
            {
                id: 'size',
                name: videoListT('columnSize'),
                width: 90,
                template: (item) => <Text variant="body-1">{formatFileSize(item.file.size)}</Text>,
            },
            {
                id: 'summarization',
                name: videoListT('columnSummarization'),
                width: 100,
                align: 'center',
                template: (item) => (
                    <Switch
                        checked={item.enableSummarization}
                        onUpdate={(checked) => {
                            if (selectedIds.has(item.id) && selectedIds.size > 1) {
                                handleBulkSummarizationChange(checked);
                            } else {
                                onToggleSummarization(item.id);
                            }
                        }}
                        disabled={isUploading || item.status === 'success'}
                    />
                ),
            },
        ];

        baseColumns.push({
            id: 'status',
            name: videoListT('columnStatus'),
            width: 100,
            align: 'center',
            template: (item) => getStatusLabel(item),
        });

        baseColumns.push({
            id: 'progress',
            name: videoListT('columnProgress'),
            width: 120,
            template: (item) => (
                <div style={{ width: '100%' }}>
                    {item.status === 'uploading' ? (
                        <Progress
                            theme="success"
                            value={item.progress}
                            size="m"
                            text={`${item.progress}%`}
                        />
                    ) : item.status === 'success' ? (
                        <Progress theme="success" value={100} size="m" text="100%" />
                    ) : item.status === 'error' ? (
                        <Progress theme="danger" value={100} size="m" text={statusesT('error')} />
                    ) : (
                        <Progress
                            theme="misc"
                            value={item.progress}
                            size="m"
                            text={item.progress ? `${item.progress}%` : undefined}
                        />
                    )}
                </div>
            ),
        });

        baseColumns.push({
            id: 'actions',
            name: '',
            width: 100,
            template: (item) => (
                <Flex gap={1}>
                    {item.status !== 'success' && (
                        <Button
                            view="flat-action"
                            size="s"
                            onClick={() => onStartSingle(item.id)}
                            disabled={isUploading || !item.profile}
                        >
                            <Icon data={CirclePlay} size={16} />
                        </Button>
                    )}
                    <Button
                        view="flat-danger"
                        size="s"
                        onClick={() => onRemove(item.id)}
                        disabled={item.status === 'uploading'}
                    >
                        <Icon data={TrashBin} size={16} />
                    </Button>
                </Flex>
            ),
        });

        return baseColumns;
    }, [
        isUploading,
        profileOptions,
        selectedIds,
        allEditableSelected,
        someSelected,
        editableFiles.length,
        onUpdateTitle,
        onUpdateProfile,
        onToggleSummarization,
        onStartSingle,
        onRemove,
        handleSelectAll,
        handleSelectOne,
        handleBulkProfileChange,
        handleBulkSummarizationChange,
    ]);

    const pendingFiles = files.filter((f) => f.status === 'pending' || f.status === 'error');

    const allFilesValid =
        pendingFiles.length > 0 &&
        pendingFiles.every(
            (f) =>
                f.profile && f.title.length <= MAX_VIDEO_TITLE_LENGTH && (f.title || f.file.name),
        );

    return (
        <div className="multiple-upload-table">
            <div className="multiple-upload-table__header">
                <Flex gap={2} alignItems="center" justifyContent="space-between">
                    <Text variant="subheader-2">
                        {videoUploadT('filesForUpload', { count: files.length })}
                    </Text>
                </Flex>
                <Flex gap={2} alignItems="center" style={{ marginTop: '8px' }}>
                    <Text variant="body-1" color="secondary">
                        {videoUploadT('selected', { count: selectedIds.size })}
                    </Text>
                    <Text
                        variant="body-1"
                        color="secondary"
                        style={{ opacity: someSelected ? 1 : 0.5 }}
                    >
                        — {videoUploadT('bulkEditHint')}
                    </Text>
                </Flex>
            </div>

            <div className="multiple-upload-table__content">
                <Table
                    data={paginatedFiles}
                    columns={columns}
                    verticalAlign="middle"
                    getRowId={(item) => item.id}
                    getRowClassNames={getRowClassNames}
                />
            </div>

            {files.length > pageSize && (
                <div className="multiple-upload-table__pagination">
                    <Pagination
                        page={page}
                        pageSize={pageSize}
                        total={files.length}
                        onUpdate={handlePageUpdate}
                        showInput={true}
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                    />
                </div>
            )}

            <div className="multiple-upload-table__footer">
                <Flex gap={2} justifyContent="flex-end">
                    <Button view="outlined" size="m" onClick={onCancel} disabled={isUploading}>
                        {commonT('cancel')}
                    </Button>
                    {isUploading ? (
                        <Button view="outlined-danger" size="m" onClick={onStopUpload}>
                            {commonT('stopUpload')}
                        </Button>
                    ) : (
                        <Tooltip
                            content={
                                !allFilesValid ? errorsT('allFilesMustHaveProfile') : undefined
                            }
                            disabled={allFilesValid || pendingFiles.length === 0}
                        >
                            <Button
                                view="action"
                                size="m"
                                onClick={onStartAll}
                                disabled={pendingFiles.length === 0 || !allFilesValid}
                            >
                                {commonT('uploadAll', { count: pendingFiles.length })}
                            </Button>
                        </Tooltip>
                    )}
                </Flex>
            </div>
        </div>
    );
};
